import { usePathname } from "next/navigation";
import { useEffect } from "react";
import { pageview } from "../functions/gtm";
import useIsClient from "@/hooks/useIsClient";
import useShouldFire from "./useShouldFire";

// Module-level flag — survives re-renders and remounts, resets on a full page
// reload (which is correct). Mirrors the guard in `useXPixel`.
//
// Without it every pageview fired TWICE: `PageViewPixel` flips its `refresh` state
// as soon as the gate opens (`:176`), and `refresh` is a dependency of the effect
// below, so the push ran once before the flip and once after. Observed in a live
// dataLayer as duplicate `pageview` + `home_page` pairs.
let pageViewFiredForPath: string | null = null;

export default function useGoogleTagManager(
  google_tag: string,
  refresh?: boolean,
) {
  const pathname = usePathname();
  const isClient = useIsClient();
  const shouldFire = useShouldFire();

  // Fire when EITHER container is configured. `PixelsHeadScripts` injects the tenant
  // container (`google_tag`) and the platform one (NEXT_PUBLIC_OSOSS_GTM_ID)
  // independently, but this pageview used to be gated on the tenant id alone — so a
  // store with only the platform container loaded GTM and then never pushed a single
  // app event into it, which looks exactly like "GTM is not working".
  //
  // The push goes into the one shared `dataLayer`, so it happens ONCE and both
  // containers observe it. If both are configured and both carry a tag on the same
  // trigger, dedupe belongs in GTM config, not here.
  const OSOSS_GTM_ID = process.env.NEXT_PUBLIC_OSOSS_GTM_ID ?? "";
  const hasContainer = !!google_tag || !!OSOSS_GTM_ID;

  useEffect(() => {
    if (!isClient || !hasContainer || !shouldFire || !pathname) return;
    if (pageViewFiredForPath === pathname) return;

    pageViewFiredForPath = pathname;
    pageview(pathname);
  }, [pathname, isClient, hasContainer, refresh, shouldFire]);
}
